/* NEOMACHI - SODIUM ALLEY behaviours
   Small, dependency-free custom elements layered on top of Impact's theme.js.
   - <nm-reviews>            review carousel (indicator bars)
   - <nm-load-more>          "load more" replacing pagination (Section Rendering API)
   - <nm-facet-rail>         mobile filter chips + inline panels
   - [data-nm-seo-toggle]    collapsed SEO band on mobile
   - <nm-garment-switcher>   "this design, other garment" via predictive search
   - <nm-deal-box>           live cart-based deal counter
*/
(function () {
  'use strict';

  /* ---------------- Reviews ---------------- */
  class NmReviews extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const bar = e.target.closest('.nm-reviews__bar');
        if (!bar) return;
        const idx = bar.dataset.index;
        this.querySelectorAll('.nm-review').forEach((r) => r.classList.toggle('is-active', r.dataset.index === idx));
        this.querySelectorAll('.nm-reviews__bar').forEach((b) => b.classList.toggle('is-active', b === bar));
      });
    }
  }
  if (!customElements.get('nm-reviews')) customElements.define('nm-reviews', NmReviews);

  /* ---------------- Load more ---------------- */
  class NmLoadMore extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-load-more]');
      if (!this.button) return;
      this.button.addEventListener('click', () => this.load());
    }
    async load() {
      const next = this.dataset.next;
      if (!next || this.classList.contains('is-loading')) return;
      this.classList.add('is-loading');
      try {
        const url = new URL(next, window.location.origin);
        url.searchParams.set('section_id', this.dataset.section);
        const html = await fetch(url.toString()).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newList = doc.getElementById(this.dataset.target);
        const list = document.getElementById(this.dataset.target);
        if (newList && list) {
          Array.from(newList.children).forEach((child) => list.appendChild(document.importNode(child, true)));
        }
        const newSelf = doc.querySelector('nm-load-more');
        const shown = newSelf ? newSelf.dataset.shown : this.dataset.total;
        const total = this.dataset.total;
        this.dataset.shown = shown;
        document.querySelectorAll('[data-nm-shown]').forEach((el) => (el.textContent = shown));
        const fill = this.querySelector('.nm-loadmore__fill');
        if (fill && total > 0) fill.style.width = Math.min(100, Math.round((shown / total) * 100)) + '%';
        if (newSelf && newSelf.dataset.next) {
          this.dataset.next = newSelf.dataset.next;
        } else {
          delete this.dataset.next;
          this.button.remove();
        }
        // keep the URL in sync so a reload lands on the same page
        const pageUrl = new URL(window.location.href);
        pageUrl.searchParams.set('page', url.searchParams.get('page') || '1');
        history.replaceState({}, '', pageUrl.toString());
      } catch (e) {
        console.warn('[nm] load more failed', e);
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }
  if (!customElements.get('nm-load-more')) customElements.define('nm-load-more', NmLoadMore);

  /* ---------------- Facet rail (mobile) ---------------- */
  class NmFacetRail extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const chip = e.target.closest('.nm-rail__chip');
        const close = e.target.closest('[data-close]');
        if (chip) {
          const id = chip.dataset.panel;
          const panel = this.querySelector('#' + id);
          const isOpen = panel && !panel.hidden;
          this.closeAll();
          if (panel && !isOpen) {
            panel.hidden = false;
            chip.setAttribute('aria-expanded', 'true');
          }
        } else if (close) {
          this.closeAll();
        }
      });
    }
    closeAll() {
      this.querySelectorAll('.nm-rail__panel').forEach((p) => (p.hidden = true));
      this.querySelectorAll('.nm-rail__chip').forEach((c) => c.setAttribute('aria-expanded', 'false'));
    }
  }
  if (!customElements.get('nm-facet-rail')) customElements.define('nm-facet-rail', NmFacetRail);

  /* ---------------- SEO band toggle ---------------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-nm-seo-toggle]');
    if (!btn) return;
    const section = btn.closest('.nm-seo');
    const open = section.classList.toggle('is-open');
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    btn.textContent = open ? btn.dataset.less : btn.dataset.more;
  });

  /* ---------------- Garment switcher (PDP) ---------------- */
  class NmGarmentSwitcher extends HTMLElement {
    async connectedCallback() {
      const design = (this.dataset.design || '').trim();
      const current = this.dataset.handle;
      const list = this.querySelector('[data-nm-garments]');
      if (!design || !list) return;
      try {
        const url = new URL(this.dataset.searchUrl || '/search/suggest.json', window.location.origin);
        url.searchParams.set('q', design);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '10');
        url.searchParams.set('resources[options][unavailable_products]', 'hide');
        url.searchParams.set('resources[options][fields]', 'title');
        const data = await fetch(url.toString()).then((r) => r.json());
        const prefix = design.toUpperCase() + ':';
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        const siblings = products.filter((p) => p.title.toUpperCase().startsWith(prefix));
        if (siblings.length < 2) { this.hidden = true; return; }
        const items = siblings.map((p) => {
          const garment = p.title.split(':').slice(1).join(':').trim();
          const active = p.handle === current;
          const img = p.featured_image && p.featured_image.url ? p.featured_image.url.replace(/(\.[a-z]+)(\?|$)/i, '_300x$1$2') : '';
          return '<a href="' + p.url + '" class="nm-garment ' + (active ? 'is-active' : '') + '" ' + (active ? 'aria-current="page"' : '') + '>' +
            '<span class="nm-garment__img">' + (img ? '<img src="' + img + '" alt="" loading="lazy">' : '') + '</span>' +
            '<span class="nm-garment__label">' + garment.replace(/</g, '&lt;') + '</span>' +
            '<span class="nm-garment__price">' + (this.dataset.currency || '') + p.price + '</span></a>';
        });
        list.innerHTML = items.join('');
        const count = this.querySelector('[data-nm-garment-count]');
        if (count) count.textContent = siblings.length + ' ' + (this.dataset.itemsLabel || 'ITEMS');
        this.hidden = false;
      } catch (e) {
        this.hidden = true;
      }
    }
  }
  if (!customElements.get('nm-garment-switcher')) customElements.define('nm-garment-switcher', NmGarmentSwitcher);

  /* ---------------- Deal box: live counter from cart ---------------- */
  const nmDealCategory = (type) => {
    const t = (type || '').toLowerCase().replace(/[^a-z]/g, '');
    if (t.includes('tshirt') || t.includes('tee')) return 'tee';
    if (t.includes('hoodie') || t.includes('sweater')) return 'hoodie';
    return '';
  };
  class NmDealBox extends HTMLElement {
    connectedCallback() {
      this.needed = parseInt(this.dataset.needed || '3', 10);
      this.category = this.dataset.deal || nmDealCategory(this.dataset.productType);
      this.update();
      document.addEventListener('cart:change', () => this.update());
      document.addEventListener('cart:refresh', () => this.update());
    }
    async update() {
      try {
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const inCart = cart.items
          .filter((i) => nmDealCategory(i.product_type) === this.category)
          .reduce((n, i) => n + i.quantity, 0);
        const left = Math.max(0, this.needed - inCart);
        const title = this.querySelector('[data-nm-deal-title]');
        if (!title) return;
        if (left === 0) title.textContent = this.dataset.doneText || title.textContent;
        else title.textContent = (this.dataset.titlePattern || '').replace('[n]', left) || title.textContent;
      } catch (e) { /* silent */ }
    }
  }
  if (!customElements.get('nm-deal-box')) customElements.define('nm-deal-box', NmDealBox);
})();